import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trophy, Settings, Inbox } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/auth/context/AuthContext'
import { useConfiguracionesFidelizacion } from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { ReporteCard } from '../components/ReporteCard'

const ADMIN_GROUPS = ['admin', 'fidelizacion_admin']

export function ReportesFidelizacionPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading, error } = useConfiguracionesFidelizacion({ activo: true })

  const canConfigure = useMemo(() => {
    if (!user) return false
    return user.is_superuser || ADMIN_GROUPS.some((g) => user.groups.includes(g))
  }, [user])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-6 w-6" />
            Fidelización
          </h1>
          {data && (
            <Badge className="badge-gradient text-xs">
              {data.length} reporte{data.length === 1 ? '' : 's'} activo{data.length === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
        {canConfigure && (
          <Button
            variant="outline"
            onClick={() => navigate('/reportes-fidelizacion/configuracion')}
          >
            <Settings className="mr-2 h-4 w-4" />
            Configuración
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : data && data.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((c) => (
            <ReporteCard key={c.id} configuracion={c} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Inbox className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No hay reportes configurados</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {canConfigure
              ? 'Crea tu primer reporte en Configuración.'
              : 'Pide a un administrador que configure el primer reporte.'}
          </p>
          {canConfigure && (
            <Button
              onClick={() => navigate('/reportes-fidelizacion/configuracion')}
              className="bg-blue-800 hover:bg-blue-900 text-white"
            >
              <Settings className="mr-2 h-4 w-4" />
              Ir a Configuración
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
